import React, {useEffect, useState} from "react";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getPreloadFileUrl, getResourceDownloadUrl} from "../../constants/apiEndpoints";
import Call from "../../hocs/call";
import CustomJsonForms from "../custom-json-forms";
import FullscreenDialog from "../fullscreen-dialog";
import {languageSelector, languagesSelector} from "../../state/app/appSelectors";
import {baseUrlSelector} from "../../state/config/configSelectors";
import {fileSave} from "../../state/file-save/fileSaveActions";
import {
  createResource,
  getResource,
  getResourceSchema,
  hideResource,
  updateResource
} from "../../state/package-manager/packageManagerActions";
import {packageManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const ResourceSettings = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const baseURL = useSelector(baseUrlSelector);
  const languages = useSelector(languagesSelector);
  const language = useSelector(languageSelector);
  const {isResourceSettingsModalVisible, nodeId, packageId, resourceSchema, resourceUiSchema, resourceId, resource} =
    useSelector(packageManagerSelector);

  const [data, setData] = useState(null);
  const [errors, setErrors] = useState();
  const [showValidation, setShowValidation] = useState(false);

  const fetchResourceSchema = nodeId => {
    dispatch(getResourceSchema(nodeId));
  };

  const fetchResource = ({nodeId, resourceId}) => {
    dispatch(getResource(nodeId, resourceId));
  };

  const onResourceSettingsHide = () => {
    dispatch(hideResource());
  };

  const onResourceSettingsSubmit = () => {
    if ((errors || []).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      if (resourceId) {
        dispatch(updateResource(nodeId, resourceId, data));
      } else {
        dispatch(createResource(nodeId, data));
      }
    }
  };

  const onResourceFileDownload = () => {
    dispatch(
      fileSave(
        getResourceDownloadUrl(nodeId, packageId, resourceId),
        {name: localizeI18nObj(resource.Names, language, languages), extension: resource.format},
        baseURL
      )
    );
  };

  useEffect(() => {
    setData(resource);
    setErrors(null);
    setShowValidation(false);

    return () => {
      setData(null);
      setErrors(null);
      setShowValidation(false);
    };
  }, [resource]);

  if (!nodeId || !isResourceSettingsModalVisible) {
    return null;
  }

  return (
    <FullscreenDialog
      open={isResourceSettingsModalVisible}
      title={
        resourceId === 0
          ? t("modules.packageManager.resourceSettings.createResource.label")
          : t("modules.packageManager.resourceSettings.editResource.label")
      }
      onClose={onResourceSettingsHide}
      className="resource-settings"
    >
      <DialogContent>
        <Call cb={fetchResourceSchema} cbParam={nodeId} disabled={Boolean(resourceSchema && resourceUiSchema)}>
          {resourceSchema && resourceUiSchema && (
            <Call cb={fetchResource} cbParam={{nodeId: nodeId, resourceId: resourceId}} disabled={!resourceId}>
              <CustomJsonForms
                schema={resourceSchema}
                uischema={resourceUiSchema}
                data={data}
                setData={setData}
                errors={errors}
                setErrors={setErrors}
                showValidation={showValidation}
                i18nPrefix={"modules.packageManager.resourceSettings"}
                fileHandlerUploadUrl={getPreloadFileUrl(nodeId)}
                fileHandlerDownload={onResourceFileDownload}
              />
            </Call>
          )}
        </Call>
      </DialogContent>
      <DialogActions>
        <Button className="resource-settings__actions__cancel-btn" onClick={onResourceSettingsHide}>
          {t("commons.confirm.cancel")}
        </Button>
        <Button className="resource-settings__actions__submit-btn" onClick={onResourceSettingsSubmit} disabled={!data}>
          {t("commons.confirm.submit")}
        </Button>
      </DialogActions>
    </FullscreenDialog>
  );
};

export default ResourceSettings;
